import React, { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import ModuleFrame, { Chip } from '../../components/ModuleFrame';
import { apiGet } from '../../utils/backendApi';

// Solutions — one catalog, two homes.
// Foundation cards are the app kits an admin already sees on Home.
// Firebase and external cards are the adopted portfolio: they stay on the
// host that is already stable (Firebase Hosting, or their own domain) and
// move onto this foundation later. Manage is only present when the caller
// admins that app's org (GET /solutions sets canManage).

const PLANE = {
  foundation: { label: 'Foundation', tone: 'info' },
  firebase: { label: 'Firebase', tone: 'ok' },
  external: { label: 'Own host', tone: 'muted' },
};

function AppCard({ app }) {
  const plane = PLANE[app.plane] || PLANE.external;
  return (
    <div className="mf-card">
      <div className="mf-card-title" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {app.name}
        <Chip tone={plane.tone}>{plane.label}</Chip>
        <Chip tone={app.state === 'up' ? 'ok' : app.state === 'down' ? 'danger' : 'muted'} title={app.checkedAt ? `checked ${new Date(app.checkedAt).toLocaleString()}` : ''}>{app.state}{app.latencyMs ? ` · ${app.latencyMs} ms` : ''}</Chip>
      </div>
      <div className="mf-muted">{app.blurb}</div>
      <div className="mf-muted">{app.appType || 'kit'} · {app.delivery} · {app.status}</div>
      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
        {app.url && <a className="cs-btn cs-btn-outline cs-btn-sm" href={app.url} target="_blank" rel="noreferrer">Open ↗</a>}
        {app.canManage && app.orgId && (
          <Link className="cs-btn cs-btn-outline cs-btn-sm" to={`/orgs/${app.orgId}/apps/${app.id}`}>Manage</Link>
        )}
      </div>
    </div>
  );
}

function Group({ title, help, apps }) {
  if (!apps?.length) return null;
  return (
    <section style={{ display: 'grid', gap: 8 }}>
      <div>
        <h2 style={{ fontSize: 15, margin: '4px 0 2px' }}>{title}</h2>
        <p className="mf-muted" style={{ margin: 0 }}>{help}</p>
      </div>
      <div className="mf-grid-cards" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
        {apps.map((a) => <AppCard key={`${a.plane}-${a.id}`} app={a} />)}
      </div>
    </section>
  );
}

export default function SolutionsPage() {
  const { org } = useOutletContext();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    apiGet('/solutions').then(setData).catch((e) => setErr(e.message));
  }, []);
  const groups = data?.groups || {};
  const apps = data?.apps || [];
  const up = apps.filter((a) => a.state === 'up').length;

  return (
    <ModuleFrame
      title="Solutions"
      subtitle="What has been built"
      accent="#14e0c8"
      note={`Solutions page: ${apps.length ? `${apps.length} fleet apps, ${up} up` : err ? 'fleet list unavailable' : 'loading fleet apps'}. Read-only list of what runs on the foundation; NoVA opens an app or navigates (navigate_app) to App Builder / DevOps.`}
      help="App kits already on this foundation sit with the apps that still run on Firebase or their own host. Firebase stays in place while it is the stable architecture. Anyone signed in can open an app. Manage is there only when you admin the organization that owns it — the same kits are on that org’s Home."
      context={<Chip tone={apps.length ? (up === apps.length ? 'ok' : 'warn') : 'muted'}>{apps.length ? `${up}/${apps.length} up` : err ? 'unavailable' : 'loading'}</Chip>}
      verbs={[
        { label: 'Build your own', hint: 'App Builder', to: `/orgs/${org.id}/builder`, primary: true },
        { label: 'Your instances', hint: 'DevOps', to: `/orgs/${org.id}/deployments?tab=instances` },
      ]}
    >
      {err && <p className="cs-form-error" role="alert">{err}</p>}
      {data && !apps.length && <p className="cs-help">No fleet apps are registered yet.</p>}
      <div style={{ display: 'grid', gap: 18 }}>
        <Group
          title="On this foundation"
          help="These are the app kits. An admin of the owning organization also sees them on Home."
          apps={groups.foundation}
        />
        <Group
          title="On Firebase"
          help="Stable on Firebase Hosting. They stay there until they are brought onto this foundation."
          apps={groups.firebase}
        />
        <Group
          title="On their own host"
          help="Not on Firebase Hosting, and not on this foundation yet. They stay on the host they have."
          apps={groups.external}
        />
      </div>
      <p className="mf-muted">Fleet health is probed continuously; incidents open automatically after repeated failures. <Link to={`/orgs/${org.id}/analytics`}>Analytics →</Link></p>
    </ModuleFrame>
  );
}
